import { useSyncExternalStore } from 'react';

/**
 * A QUEUE of pending human-verification requests — docs/adr/0010.
 *
 * One widget at a time, but each waiter gets its OWN token: Turnstile tokens are single-use, and
 * sharing one promise meant sharing one token.
 */
export class HumanVerificationCancelled extends Error {
  constructor() {
    super('Human verification cancelled');
    this.name = 'HumanVerificationCancelled';
  }
}

type Pending = {
  /** Identifies THIS waiter. The snapshot is this id, not a boolean — see `getSnapshot`. */
  ticket: number;
  promise: Promise<string>;
  resolve: (token: string) => void;
  reject: (e: Error) => void;
};

const queue: Pending[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());
let lastTicket = 0;

export const requestHumanVerification = (): Promise<string> => {
  let resolve!: (token: string) => void;
  let reject!: (e: Error) => void;
  const promise = new Promise<string>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  queue.push({ ticket: ++lastTicket, promise, resolve, reject });
  emit();
  return promise;
};

export const resolveHumanVerification = (token: string): void => {
  const current = queue.shift();
  emit();
  current?.resolve(token);
};

export const cancelHumanVerification = (): void => {
  const cancelled = queue.splice(0, queue.length);
  emit();
  cancelled.forEach((p) => p.reject(new HumanVerificationCancelled()));
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
/**
 * The ticket of the waiter at the head of the queue, or 0 when there is none.
 *
 * Deliberately NOT a boolean. A Turnstile token is single-use, so every waiter needs its own
 * WIDGET, and the widget is created once per mount (an empty-dependency effect). With a boolean
 * snapshot, dequeuing the first of two waiters goes `true -> true`: the dialog re-renders
 * identically, React keeps the same widget mounted, no second token is ever issued, and the
 * second waiter blocks for ever. Keying the widget on this value remounts it as the queue
 * advances, which is what makes "one widget at a time, each with its own token" true.
 */
const getSnapshot = () => queue[0]?.ticket ?? 0;

export const isHumanVerificationPending = (): boolean => getSnapshot() > 0;

/** The current waiter's ticket — use it as the challenge widget's `key`. */
export const useHumanVerificationTicket = (): number =>
  useSyncExternalStore(subscribe, getSnapshot);

export const useHumanVerificationPending = (): boolean => useHumanVerificationTicket() > 0;

export const _resetHumanVerificationForTests = (): void => {
  queue.length = 0;
  listeners.clear();
  lastTicket = 0;
};
